import {
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "../firebase";
import { getApiUrl } from "../config/api";

/**
 * Generate a username from email or display name
 */
const generateUsername = (email, displayName) => {
  // Try to use display name first
  if (displayName) {
    const baseUsername = displayName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .substring(0, 20);
    if (baseUsername.length >= 3) {
      return baseUsername;
    }
  }

  // Fallback to email username
  if (email) {
    const emailUsername = email.split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
    return emailUsername.substring(0, 20);
  }

  // Last resort: generate random username
  return `user${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Check if username is available
 */
const checkUsernameAvailability = async (username) => {
  try {
    const response = await fetch(getApiUrl(`/users/${username}`));
    return response.status === 404; // Available if not found
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error("Error checking username:", error);
    }
    return false;
  }
};

/**
 * Find an available username by appending numbers
 */
const findAvailableUsername = async (baseUsername) => {
  let username = baseUsername;
  let counter = 1;
  let maxAttempts = 100;

  while (counter < maxAttempts) {
    const isAvailable = await checkUsernameAvailability(username);
    if (isAvailable) {
      return username;
    }
    username = `${baseUsername}${counter}`;
    counter++;
  }

  // If we can't find one, use timestamp
  return `${baseUsername}${Date.now()}`;
};

/**
 * Create user profile in backend after OAuth sign-in
 */
const createOAuthUserProfile = async (firebaseUser) => {
  const displayName = firebaseUser.displayName || "";
  const nameParts = displayName.split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // Generate username
  const baseUsername = generateUsername(firebaseUser.email, displayName);
  const username = await findAvailableUsername(baseUsername);

  const userData = {
    email: firebaseUser.email,
    firstName: firstName,
    lastName: lastName,
    username: username,
    uid: firebaseUser.uid,
    points: {
      generalPoints: 0,
      postPoints: 0,
      reviewPoints: 0,
    },
    playlists: [],
    emailVerified: firebaseUser.emailVerified,
    achievements: [],
    numOfLists: 0,
  };

  const response = await fetch(getApiUrl("/users"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // If user already exists, try to fetch their data instead
    if (response.status === 400 && errorData.detail && errorData.detail.includes("already exists")) {
      // User already exists, fetch their data
      const token = await firebaseUser.getIdToken();
      const fetchResponse = await fetch(getApiUrl(`/users/auth/${firebaseUser.uid}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (fetchResponse.ok) {
        return await fetchResponse.json();
      }
    }
    
    throw new Error(errorData.detail || `Failed to create user profile: ${response.status}`);
  }

  return await response.json();
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Get Firebase ID token for authentication
    const token = await user.getIdToken();

    // Check if user exists in backend with authentication token
    const response = await fetch(getApiUrl(`/users/auth/${user.uid}`), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      // User exists, return user data
      const userData = await response.json();
      localStorage.setItem("userData", JSON.stringify(userData));
      return { success: true, userData, isNewUser: false };
    } else if (response.status === 404) {
      // User doesn't exist (404), create profile
      const userData = await createOAuthUserProfile(user);
      localStorage.setItem("userData", JSON.stringify(userData));
      return { success: true, userData, isNewUser: true };
    } else {
      // Other error (401, 403, etc.)
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to check user: ${response.status}`);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error("Google sign-in error:", error);
    }
    throw error;
  }
};

